import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { ChevronLeft, ChevronRight, User as UserIcon, MapPin, Receipt, ShieldCheck, LogOut, HelpCircle, LogIn, Edit3, Save, MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/lib/seo";
import * as ella from "@/lib/ellaApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AccountPage() {
  const nav = useNavigate();
  const ctx = useOutletContext();
  const { user, isAuthed, logout, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);

  useSeo({ title: "Akun Saya" });

  const startEdit = () => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
    setEmail(user?.email || "");
    setEditing(true);
  };

  if (!isAuthed) {
    return (
      <div className="p-6 text-center pb-32">
        <UserIcon className="w-16 h-16 text-gray-300 mx-auto mb-3" />
        <h2 className="font-display text-xl font-bold">Akun Saya</h2>
        <p className="text-sm text-gray-500 mt-1 mb-5">Masuk dulu untuk melihat data akunmu.</p>
        <Button onClick={() => ctx?.openAuth?.()} className="btn-primary h-11 px-6" data-testid="account-login-button">
          <LogIn className="w-4 h-4 mr-2" /> Masuk / Daftar
        </Button>
      </div>
    );
  }

  const save = async () => {
    // The API rewrites all three columns — never send an empty one
    if (!name) { toast.error("Nama Lengkap tidak boleh kosong"); return; }
    if (!phone) { toast.error("No. Telepon tidak boleh kosong"); return; }
    if (!email) { toast.error("Email tidak boleh kosong"); return; }
    setSaving(true);
    try {
      const res = await ella.doUpdateUser({ name, phone, email });
      if (res.status) {
        toast.success(res.message || "Data diri tersimpan");
        await refreshUser();
        setEditing(false);
      } else toast.error(res.message || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const menus = [
    { to: "/account/addresses", icon: MapPin, label: "Daftar Alamat", desc: "Kelola alamat pengiriman", testId: "menu-addresses" },
    { to: "/account/transactions", icon: Receipt, label: "Riwayat Transaksi", desc: "Lihat semua pesananmu", testId: "menu-transactions" },
    { to: "/messages", icon: MessageCircle, label: "Pesan Admin Cabang", desc: "Chat langsung dengan admin", testId: "menu-messages" },
    { to: "/privacy-policy", icon: ShieldCheck, label: "Perlindungan Data Pelanggan", desc: "Kebijakan Privasi Ella Froze", testId: "menu-privacy" },
    { to: "/help", icon: HelpCircle, label: "Bantuan", desc: "FAQ & hubungi customer service", testId: "menu-help" },
  ];

  return (
    <div className="pb-32">
      <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
        <h1 className="font-display text-lg font-bold text-gray-900">Akun Saya</h1>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-gradient-to-br from-[#D32F2F] to-[#B71C1C] rounded-2xl p-4 text-white relative overflow-hidden">
          <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -right-12 -top-12 w-24 h-24 rounded-full bg-white/5" />
          <div className="flex items-center gap-3 relative">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-lg" data-testid="account-name">{user?.name || "User"}</p>
              <p className="text-sm text-white/80 truncate" data-testid="account-email">{user?.email}</p>
            </div>
          </div>
        </div>

        <section className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-sm">Data Diri</h2>
            {!editing ? (
              <button onClick={startEdit} className="text-[#D32F2F] text-xs font-semibold flex items-center gap-1" data-testid="edit-profile-button">
                <Edit3 className="w-3.5 h-3.5" /> Ubah
              </button>
            ) : (
              <button onClick={save} disabled={saving} className="text-[#D32F2F] text-xs font-semibold flex items-center gap-1" data-testid="save-profile-button">
                <Save className="w-3.5 h-3.5" /> {saving ? "Menyimpan..." : "Simpan"}
              </button>
            )}
          </div>
          {editing ? (
            <div className="space-y-2">
              <div><Label className="text-xs">Nama Lengkap</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></div>
              <div><Label className="text-xs">No. WhatsApp</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email-input" /></div>
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Nama</span><span className="font-medium">{user?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium">{user?.email}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">WhatsApp</span><span className="font-medium">{user?.phone || "-"}</span></div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {menus.map(({ to, icon: Icon, label, desc, testId }) => (
            <Link key={to} to={to} className="flex items-center gap-3 p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50" data-testid={testId}>
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#D32F2F]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-gray-500">{desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </Link>
          ))}
        </section>

        <Button
          variant="outline"
          onClick={async () => { await logout(); toast.success("Berhasil keluar"); nav("/"); }}
          className="w-full h-11 rounded-full text-[#D32F2F] border-red-200 hover:bg-red-50"
          data-testid="account-logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" /> Keluar Akun
        </Button>
      </div>
    </div>
  );
}
